import type { ConditionalConfig } from '@viapeople/components/src/components/forms/conditionalTypes'

/** Map from component ID to its ConditionalConfig (undefined = unconditional) */
export type ComponentConditionalMap = Record<string, ConditionalConfig | undefined>

/**
 * Returns true if adding a dependency from targetComponentId → sourceComponentId
 * would create a cycle. I.e., source already depends (directly or transitively) on target.
 */
export function detectCircularDependency(
	targetComponentId: string,
	sourceComponentId: string,
	allConditionals: ComponentConditionalMap,
): boolean {
	if (targetComponentId === sourceComponentId) return true
	const visited = new Set<string>()
	const queue = [sourceComponentId]
	while (queue.length > 0) {
		const current = queue.shift()
		if (current === undefined) continue
		if (current === targetComponentId) return true
		if (visited.has(current)) continue
		visited.add(current)
		const config = allConditionals[current]
		if (config) {
			for (const condition of config.conditions) {
				queue.push(condition.sourceComponentId)
			}
		}
	}
	return false
}

/**
 * Returns the set of all component IDs that (directly or transitively)
 * list componentId as a source in their conditions.
 * Used to filter the source dropdown in the admin editor.
 */
export function getTransitiveDependents(componentId: string, allConditionals: ComponentConditionalMap): Set<string> {
	// Build reverse adjacency: sourceId → set of componentIds that depend on it
	const reverseDeps = new Map<string, Set<string>>()
	for (const [compId, config] of Object.entries(allConditionals)) {
		if (!config) continue
		for (const condition of config.conditions) {
			if (!reverseDeps.has(condition.sourceComponentId)) {
				reverseDeps.set(condition.sourceComponentId, new Set())
			}
			reverseDeps.get(condition.sourceComponentId)?.add(compId)
		}
	}

	// BFS from componentId through reverse edges
	const dependents = new Set<string>()
	const queue = [componentId]
	const visited = new Set<string>()
	while (queue.length > 0) {
		const current = queue.shift()
		if (current === undefined) continue
		if (visited.has(current)) continue
		visited.add(current)
		const deps = reverseDeps.get(current)
		if (deps) {
			for (const dep of deps) {
				dependents.add(dep)
				queue.push(dep)
			}
		}
	}
	return dependents
}

export interface ConditionalTreeNode {
	id: string
	custom: Record<string, unknown>
	children?: ConditionalTreeNode[]
}

/**
 * Builds a ComponentConditionalMap from the admin formComponents tree.
 * Walks children recursively.
 */
export function buildConditionalMap(formComponents: ConditionalTreeNode[]): ComponentConditionalMap {
	const map: ComponentConditionalMap = {}
	const walk = (components: ConditionalTreeNode[]) => {
		for (const comp of components) {
			map[comp.id] = (comp.custom as { conditional?: ConditionalConfig }).conditional
			if (comp.children) walk(comp.children)
		}
	}
	walk(formComponents)
	return map
}
